import { ExpandMore } from "@mui/icons-material";
import { Button, Chip, Menu, MenuItem, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useCoverageOverview } from "@platform/coverage-overview";
import React, { useState } from "react";
import { usePlanDesignerRfpIds } from "@platform/plan-grid-core";
import { COVERAGE_COMPLETION_STATUSES } from "@platform/types";

import { useApiClient } from "../../api";
import {
  buildCoverageCompletionTooltipMessage,
  computeCompletionGate,
} from "../../util/coverageCompletionTooltip";

import type { BenefitPlanTypeEnum, CoverageTypeEnum } from "@platform/api";
import type {
  CoverageStatus,
  CoverageStatusKey,
  MissingField,
} from "@platform/plan-grid-core";

type Option = { value: string; label: string };
type CustomColor = "default" | "primary" | "success" | "warning" | "info";
type ColorStyles = {
  backgroundColor: string;
  color: string;
  borderColor: string;
};

const AWAITING_INPUT = COVERAGE_COMPLETION_STATUSES.awaitingInput;
const COMPLETE = COVERAGE_COMPLETION_STATUSES.complete;
const MANUAL_COMPLETION = "manual-completion";
const PARTIALLY_EXTRACTED = "partially-extracted";

const STATUS_LABELS: Record<string, string> = {
  [AWAITING_INPUT]: "Awaiting Input",
  [COMPLETE]: "Complete",
  [MANUAL_COMPLETION]: "Manual Completion",
  [PARTIALLY_EXTRACTED]: "Partially Extracted",
};

const BASE_OPTIONS: Option[] = [
  { value: AWAITING_INPUT, label: "Awaiting Input" },
  { value: COMPLETE, label: "Mark as Complete" },
];

const REVERT_OPTIONS: Record<string, Option> = {
  [PARTIALLY_EXTRACTED]: {
    value: PARTIALLY_EXTRACTED,
    label: "Partially Extracted",
  },
  [MANUAL_COMPLETION]: {
    value: MANUAL_COMPLETION,
    label: "Manual Completion",
  },
};

/**
 * Builds the dropdown options based on the derived (AI Dox-only) status.
 * Always includes Awaiting Input and Complete. Additionally includes the
 * revert option matching the derived status so the user can undo a manual
 * override and return the coverage to its natural state.
 */
const buildOptions = (derivedStatusKey?: CoverageStatusKey): Option[] => {
  const revertValue = mapExternalStatusToSelectValue(derivedStatusKey);
  const revertOption = revertValue ? REVERT_OPTIONS[revertValue] : undefined;

  return revertOption ? [...BASE_OPTIONS, revertOption] : [...BASE_OPTIONS];
};

const mapExternalStatusToSelectValue = (
  statusKey?: CoverageStatusKey,
): string | undefined => {
  switch (statusKey) {
    case "complete":
      return COMPLETE;
    case "awaiting-input":
      return AWAITING_INPUT;
    case "manual-review-needed":
      return MANUAL_COMPLETION;
    case "partially-pre-populated":
      return PARTIALLY_EXTRACTED;
    default:
      return undefined;
  }
};

const getStatusColor = (status: string): CustomColor => {
  switch (status) {
    case COMPLETE:
      return "success";
    case AWAITING_INPUT:
      return "primary";
    case MANUAL_COMPLETION:
      return "warning";
    case PARTIALLY_EXTRACTED:
      return "info";
    default:
      return "default";
  }
};

export interface CoverageStatusSelectButtonProps {
  coverageType: string;
  productType?: string;
  disabled?: boolean;
  hasEligibilityClasses?: boolean;
  hasAllRequiredFields?: boolean;
  missingFields?: MissingField[];
  /** True when two plan groups share a Current Policy; blocks marking complete (ADR 10.22). */
  hasDuplicateCurrentPolicy?: boolean;
  onSuccess: () => void;
  onError: (error: Error) => void;
  status?: CoverageStatus;
}

export const CoverageStatusSelectButton: React.FC<
  CoverageStatusSelectButtonProps
> = ({
  coverageType,
  productType,
  status,
  disabled = false,
  hasEligibilityClasses = true,
  hasAllRequiredFields = true,
  missingFields = [],
  hasDuplicateCurrentPolicy = false,
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const axiosInstance = useApiClient();
  const { ecoRfpId } = usePlanDesignerRfpIds();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const { setCompletionStatus, isSettingCompletion } = useCoverageOverview({
    ecoRfpId: ecoRfpId ?? "",
    axiosInstance,
  });

  const options = buildOptions(status?.derivedStatusKey);

  const btxStatus = mapExternalStatusToSelectValue(status?.key);
  const isComplete = btxStatus === COMPLETE ? true : false;
  const completionGate = computeCompletionGate({
    isComplete,
    hasEligibilityClasses,
    hasAllRequiredFields,
    hasDuplicateCurrentPolicy,
  });

  const selectedOption = options.find((option) => option.value === btxStatus);
  const selectedLabel = selectedOption?.label ?? "Select Status";
  const chipStatusLabel = btxStatus
    ? (STATUS_LABELS[btxStatus] ?? btxStatus)
    : selectedLabel;
  const statusColor = getStatusColor(btxStatus ?? "");
  const aiChips = (
    theme.palette.ai as typeof theme.palette.ai & {
      chips: {
        default: ColorStyles;
        primary: ColorStyles;
        success: ColorStyles;
        warning: ColorStyles;
        info: ColorStyles;
      };
    }
  ).chips;
  const styles = {
    default: aiChips.default,
    primary: aiChips.primary,
    success: aiChips.success,
    warning: aiChips.warning,
    info: aiChips.info,
  }[statusColor];

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleItemClick = async (nextValue: string): Promise<void> => {
    handleMenuClose();
    try {
      const apiStatus =
        nextValue === COMPLETE
          ? COMPLETE
          : nextValue === AWAITING_INPUT
            ? AWAITING_INPUT
            : COVERAGE_COMPLETION_STATUSES.incomplete;

      await setCompletionStatus({
        coverageType: coverageType as CoverageTypeEnum,
        productType: productType as BenefitPlanTypeEnum | undefined,
        status: apiStatus,
      });
      onSuccess();
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      onError(errorObj);
    }
  };

  const getDisabledOptionTooltip = (optionValue: string): string => {
    if (optionValue !== COMPLETE) {
      return "";
    }

    return buildCoverageCompletionTooltipMessage({
      isMissingEligibilityClasses: completionGate.isMissingEligibilityClasses,
      isMissingRequiredFields: completionGate.isMissingRequiredFields,
      isDuplicateCurrentPolicy: completionGate.isDuplicateCurrentPolicy,
      missingFields,
    });
  };

  return (
    <>
      {/* TODO(ds-button): no DS variant for this status colour */}
      <Button
        variant="outlined"
        size="small"
        endIcon={<ExpandMore />}
        onClick={handleMenuOpen}
        disabled={disabled || isSettingCompletion || !ecoRfpId}
        color="info"
      >
        {isSettingCompletion ? "Loading" : `STATUS (${chipStatusLabel})`}
      </Button>

      <Menu anchorEl={anchorEl} open={isOpen} onClose={handleMenuClose}>
        {options.map((option) => {
          const isCompleteOption = option.value === COMPLETE;
          const isCurrentStatusOption = option.value === btxStatus;
          const disableOption =
            isCurrentStatusOption ||
            (isCompleteOption && completionGate.isBlocked);
          const tooltipMessage = getDisabledOptionTooltip(option.value);
          const hasTooltip = Boolean(tooltipMessage);

          return (
            <Tooltip
              key={option.value}
              title={tooltipMessage}
              arrow
              disableHoverListener={!hasTooltip}
              slotProps={{
                tooltip: { sx: { whiteSpace: "pre-line", maxWidth: 320 } },
              }}
            >
              <span>
                <MenuItem
                  selected={option.value === btxStatus}
                  disabled={disableOption || isSettingCompletion}
                  onClick={() => {
                    void handleItemClick(option.value);
                  }}
                >
                  {option.label}
                </MenuItem>
              </span>
            </Tooltip>
          );
        })}
      </Menu>

      {btxStatus && (
        <Chip
          label={isSettingCompletion ? "loading" : chipStatusLabel}
          sx={{
            borderRadius: "16px",
            fontWeight: 500,
            backgroundColor: styles.backgroundColor,
            color: styles.color,
            border: `1px solid ${styles.borderColor}`,
          }}
        />
      )}
    </>
  );
};
